import React, { useCallback, useRef, useState } from "react";
import { Route, Routes, Navigate, useLocation } from "react-router-dom";
import Attribute from "./components/Attributes/Attribute";
import Layout from "./components/Layout/Layout";
import "./Main.scss";
import Result from "./components/Results/Result";
import Feedback from "./components/Feedback/Feedback";
import Logout from "./auth/logout/Logout";
import Header from "./components/shared/Header/Header";
import ProtectedRoute from "./auth/ProtectedRoute";
import AuthProvider from "./auth/AuthProvider";
import CreateProject from "./components/HomePage/CreateProject";
import MsalAuthLogin from "./auth/msal/src/auth/msal/MsalAuthLogin";
import ResultsScreen from "./components/Results/ResultsScreen";
import Molecule3DPage from "./components/Results/Molecule3DPage";
import { MsalAuthenticationTemplate } from "@azure/msal-react";
import { loginRequest } from "./auth/msal/MsalAuthConfig";
import { InteractionType } from "@azure/msal-browser";
import GlideMolecularDataPage from "./components/Results/GlideMolecularDataPage";
import PhaseMolecularDataPage from "./components/Results/PhaseMolecularDataPage";
import AuthManager from "./store/AuthManager";
import AdmeMolecularDataPage from "./components/Results/AdmeMolecularDataPage";
import { usePaginationFix } from "./components/hooks/usePaginationFix";
import BatchDetailsPage from "./components/Results/batch/BatchDetailsPage";
import FragmentationPage from "./components/Results/FragmentationPage";
import SubmitJobPage from "./components/Results/SubmitJobPage";
import Glide3DPage from "./components/Results/Glide3DPage";
import ErrorBoundary from "./ErrorHandling/ErrorBoundary";
import ErrorPage from "./ErrorHandling/ErrorPage";


const AttributePageWithTitle = ({ mode }) => {
  const { state } = useLocation();
  const backHandlerRef = useRef(null);
  const [hasCustomBackHandler, setHasCustomBackHandler] = useState(false);
  const title = state?.isReTrigger
    ? "Re-Trigger Experiment"
    : state?.isClone || state?.isCloneMode
    ? "Clone Experiment"
    : mode === "edit"
    ? "Edit Generation"
    : "Submit New Generation";

  const registerBackHandler = useCallback((handler) => {
    backHandlerRef.current = typeof handler === "function" ? handler : null;
    setHasCustomBackHandler(Boolean(handler));
  }, []);

  const handleBack = useCallback(() => {
    backHandlerRef.current?.();
  }, []);

  return (
    <Layout title={title} onBack={hasCustomBackHandler ? handleBack : undefined}>
      <Attribute onBackHandlerChange={registerBackHandler} />
    </Layout>
  );
};

const LoadingComponent = () => <p>Authentication in progress...</p>;

const ErrorComponent = ({ error }) => (
  <p>An Error Occurred: {error.errorMessage}</p>
);

export default function Main() {
  usePaginationFix();
  const location = useLocation();
  if (location.pathname.startsWith("/logout")) {
    return <Logout />;
  }
  if (location.pathname === "/") {
    return <MsalAuthLogin />;
  }
  return (
    <MsalAuthenticationTemplate
      interactionType={InteractionType.Redirect}
      authenticationRequest={loginRequest}
      errorComponent={ErrorComponent}
      loadingComponent={LoadingComponent}
    >
      <AuthProvider />
      <AuthManager />
      <Header />
       <ErrorBoundary>
 <div className="main_content">
        <Routes>
          <Route
            path="/create-project"
            element={
              <ProtectedRoute>
                <CreateProject />
              </ProtectedRoute>
            }
          />
          <Route
            path="/project/:projectId/batch/:batchId"
            element={<BatchDetailsPage />}
          />
          <Route
            path="/project/:projectId/batch/:batchId/submit"
            element={<SubmitJobPage />}
          />
          <Route
            path="/project/:projectId/batch/:batchId/glide-results/:recordId"
            element={<GlideMolecularDataPage />}
          />
          <Route
            path="/project/:projectId/batch/:batchId/adme-results/:recordId"
            element={<AdmeMolecularDataPage />}
          />
          <Route
            path="/project/:projectId/batch/:batchId/results"
            element={<ResultsScreen />}
          />
         <Route
            path="/project/:projectId/batch/:batchId/phase-results/:recordId"
            element={<PhaseMolecularDataPage />}
          />
          <Route
            path="/project/:projectId/batch/:batchId/molecule3d/:recordId"
            element={<Molecule3DPage />}
          />
           <Route
            path="/project/:projectId/batch/:batchId/glide3d/:recordId"
            element={<Glide3DPage />}
          />
          <Route
            path="/attributes"
            element={
              <ProtectedRoute>
                <AttributePageWithTitle mode="create" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/attributes/:batchId/edit"
            element={
              <ProtectedRoute>
                <AttributePageWithTitle mode="edit" />
              </ProtectedRoute>
            }
          />
          <Route
            path="/:projectId/result"
            element={
              <ProtectedRoute>
                <Layout title="View Results">
                  <Result />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route
        path="/:projectId/fragmentation"
        element={
          <ProtectedRoute>
            <Layout title="Fragmentation">
              <FragmentationPage />
            </Layout>
          </ProtectedRoute>
        }
      />
          <Route
            path="/feedback"
            element={
              <ProtectedRoute>
                <Layout title="Provide Feedback">
                  <Feedback />
                </Layout>
              </ProtectedRoute>
            }
          />
          <Route path="/logout" element={<Logout />} />
          <Route path="*" element={<Navigate to="/" replace />} />
           <Route path="/error" element={<ErrorPage />} />
        </Routes>
      </div>
       </ErrorBoundary>
     
    </MsalAuthenticationTemplate>
  );
}
